import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Image,
  Dimensions,
  Platform,
  Animated,
} from 'react-native';
import { db, functions } from '../config/firebase';
import { doc, getDoc, collection, addDoc, updateDoc, serverTimestamp, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Icon from 'react-native-vector-icons/Feather';

const { width, height } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = width < 768;

// Colors - matches HandyHawk branding
const colors = {
  darkNavy: '#0A0E14',
  navy: '#13192B',
  navyLight: '#1E2640',
  teal: '#41B69A',
  gold: '#FCDB43',
  white: '#FFFFFF',
  text: '#E0E0E0',
  textMuted: '#8B8FA3',
  gray: '#6B7280',
  green: '#10B981',
  red: '#EF4444',
  orange: '#F59E0B',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const CheckoutScreen = ({ clientId, jobId }) => {
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [job, setJob] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [step, setStep] = useState(1); // 1: Review, 2: Testimonial (optional), 3: Payment, 4: Success
  const [wantsTestimonial, setWantsTestimonial] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  
  // Line items for the invoice
  const [lineItems, setLineItems] = useState([
    { description: 'Service Call', quantity: 1, price: 75 },
  ]);
  const [laborHours, setLaborHours] = useState(0);
  const [laborRate, setLaborRate] = useState(75);
  const [materialsCost, setMaterialsCost] = useState(0);
  const [discount, setDiscount] = useState(0);
  
  useEffect(() => {
    loadData();
    
    // Fade in animation
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 600,
      useNativeDriver: true,
    }).start();
  }, [clientId, jobId]);
  
  const loadData = async () => {
    try {
      // Load client
      if (clientId) {
        const clientDoc = await getDoc(doc(db, 'clients', clientId));
        if (clientDoc.exists()) {
          setClient({ id: clientDoc.id, ...clientDoc.data() });
        }
      }
      
      // Load job if provided
      if (jobId) {
        const jobDoc = await getDoc(doc(db, 'events', jobId));
        if (jobDoc.exists()) {
          const jobData = { id: jobDoc.id, ...jobDoc.data() };
          setJob(jobData);
          
          // Pre-populate line items from job if available
          if (jobData.lineItems) {
            setLineItems(jobData.lineItems);
          }
          if (jobData.laborHours) setLaborHours(jobData.laborHours);
          if (jobData.materialsCost) setMaterialsCost(jobData.materialsCost);
        }
      }
      
      // Load payment settings
      const settingsDoc = await getDoc(doc(db, 'settings', 'payment'));
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        setPaymentSettings(settings);
        if (settings.defaultLaborRate) setLaborRate(settings.defaultLaborRate);
      }
    } catch (error) {
      console.error('Error loading checkout data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate totals
  const itemsSubtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  const laborTotal = laborHours * laborRate;
  const subtotal = itemsSubtotal + laborTotal + materialsCost;
  const testimonialDiscount = wantsTestimonial ? (subtotal * (paymentSettings?.testimonialDiscount || 10) / 100) : 0;
  const total = subtotal - testimonialDiscount - discount;
  
  const handlePaymentComplete = async () => {
    setProcessing(true);
    
    try {
      // Save payment record
      const paymentData = {
        clientId: client?.id,
        clientName: client?.name,
        jobId: job?.id,
        lineItems,
        laborHours,
        laborRate,
        materialsCost,
        subtotal,
        discount: testimonialDiscount + discount,
        total,
        hasTestimonial: wantsTestimonial,
        paymentMethod: selectedPaymentMethod,
        status: 'completed',
        createdAt: serverTimestamp(),
      };
      
      await addDoc(collection(db, 'payments'), paymentData);
      
      // Update client stats
      if (client?.id) {
        const clientRef = doc(db, 'clients', client.id);
        await updateDoc(clientRef, {
          totalJobs: (client.totalJobs || 0) + 1,
          totalSpent: (client.totalSpent || 0) + total,
          lastJob: new Date().toISOString().split('T')[0],
          updatedAt: serverTimestamp(),
        });
      }
      
      // Update job status
      if (job?.id) {
        await updateDoc(doc(db, 'events', job.id), {
          status: 'completed',
          paidAt: serverTimestamp(),
          paymentAmount: total,
        });
      }
      
      // Add testimonial if provided
      if (wantsTestimonial && reviewText.trim()) {
        await addDoc(collection(db, 'testimonials'), {
          name: client?.name || 'Anonymous',
          location: client?.address?.split(',')[1]?.trim() || 'Las Vegas, NV',
          rating,
          text: reviewText,
          active: false, // Needs admin approval
          source: 'checkout',
          clientId: client?.id,
          createdAt: serverTimestamp(),
        });
      }
      
      setPaymentComplete(true);
      setStep(4); // Success
    } catch (error) {
      console.error('Error processing payment:', error);
      alert('There was an error processing your payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };
  
  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <View style={styles.loadingContent}>
          <ActivityIndicator size="large" color={colors.teal} />
          <Text style={styles.loadingText}>Loading checkout...</Text>
        </View>
      </View>
    );
  }
  
  // Raw string state for decimal inputs (keeps "75." or "75.5" visible while typing)
  const [priceStrings, setPriceStrings] = useState({});
  const [laborHoursStr, setLaborHoursStr] = useState('');
  const [laborRateStr, setLaborRateStr] = useState('');
  const [materialsCostStr, setMaterialsCostStr] = useState('');
  
  // Line item management functions
  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, price: 0 }]);
  };
  
  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    if (field === 'quantity') {
      // Allow decimal quantities
      const numVal = value.replace(/[^0-9.]/g, '');
      updated[index][field] = numVal === '' ? 0 : parseFloat(numVal) || 0;
    } else if (field === 'price') {
      // Keep raw string for display, parse number for calculations
      const cleanVal = value.replace(/[^0-9.]/g, '');
      setPriceStrings(prev => ({ ...prev, [index]: cleanVal }));
      updated[index][field] = cleanVal === '' ? 0 : parseFloat(cleanVal) || 0;
    } else {
      updated[index][field] = value;
    }
    setLineItems(updated);
  };
  
  const getPriceDisplay = (index, price) => {
    // If user is actively typing, show raw string
    if (priceStrings[index] !== undefined) {
      return priceStrings[index];
    }
    // Otherwise show the numeric value
    return price > 0 ? price.toString() : '';
  };
  
  const deleteLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
      // Clean up price string tracking
      const newPriceStrings = { ...priceStrings };
      delete newPriceStrings[index];
      setPriceStrings(newPriceStrings);
    }
  };
  
  // Decimal-friendly setters for labor and materials
  const handleLaborHoursChange = (val) => {
    const cleanVal = val.replace(/[^0-9.]/g, '');
    setLaborHoursStr(cleanVal);
    setLaborHours(cleanVal === '' ? 0 : parseFloat(cleanVal) || 0);
  };
  
  const handleLaborRateChange = (val) => {
    const cleanVal = val.replace(/[^0-9.]/g, '');
    setLaborRateStr(cleanVal);
    setLaborRate(cleanVal === '' ? 0 : parseFloat(cleanVal) || 0);
  };
  
  const handleMaterialsCostChange = (val) => {
    const cleanVal = val.replace(/[^0-9.]/g, '');
    setMaterialsCostStr(cleanVal);
    setMaterialsCost(cleanVal === '' ? 0 : parseFloat(cleanVal) || 0);
  };
  
  // Step 1: Review Invoice
  const renderReviewStep = () => (
    <View style={styles.stepContainer}>
      <View style={styles.invoiceCard}>
        <View style={styles.invoiceHeader}>
          <Text style={styles.invoiceTitle}>Invoice</Text>
          <Text style={styles.invoiceDate}>{new Date().toLocaleDateString()}</Text>
        </View>
        
        {/* Client Info */}
        <View style={styles.invoiceClientBox}>
          <Text style={styles.invoiceClientLabel}>Bill To:</Text>
          <Text style={styles.invoiceClientName}>{client?.name || 'Guest'}</Text>
          {client?.address && <Text style={styles.invoiceClientAddress}>{client.address}</Text>}
        </View>
        
        {/* Line Items - Editable */}
        <View style={styles.invoiceItems}>
          <View style={styles.invoiceItemsHeaderRow}>
            <Text style={styles.invoiceItemsSectionTitle}>Line Items</Text>
            <TouchableOpacity style={styles.addLineItemBtn} onPress={addLineItem}>
              <Icon name="plus" size={16} color={colors.teal} />
              <Text style={styles.addLineItemBtnText}>Add Item</Text>
            </TouchableOpacity>
          </View>
          
          {lineItems.map((item, idx) => (
            <View key={idx} style={styles.editableItemRow}>
              <View style={styles.editableItemMain}>
                <TextInput
                  style={styles.editableItemDescription}
                  placeholder="Service description..."
                  placeholderTextColor={colors.gray}
                  value={item.description}
                  onChangeText={(val) => updateLineItem(idx, 'description', val)}
                />
                <View style={styles.editableItemNumbers}>
                  <View style={styles.editableItemField}>
                    <Text style={styles.editableItemLabel}>Qty</Text>
                    <TextInput
                      style={styles.editableItemInput}
                      placeholder="1"
                      placeholderTextColor={colors.gray}
                      value={item.quantity > 0 ? item.quantity.toString() : ''}
                      onChangeText={(val) => updateLineItem(idx, 'quantity', val)}
                      keyboardType={isWeb ? 'default' : 'decimal-pad'}
                    />
                  </View>
                  <View style={styles.editableItemField}>
                    <Text style={styles.editableItemLabel}>Price ($)</Text>
                    <TextInput
                      style={styles.editableItemInput}
                      placeholder="0.00"
                      placeholderTextColor={colors.gray}
                      value={getPriceDisplay(idx, item.price)}
                      onChangeText={(val) => updateLineItem(idx, 'price', val)}
                      keyboardType={isWeb ? 'default' : 'decimal-pad'}
                    />
                  </View>
                  <View style={styles.editableItemTotal}>
                    <Text style={styles.editableItemLabel}>Total</Text>
                    <Text style={styles.editableItemTotalValue}>${(item.quantity * item.price).toFixed(2)}</Text>
                  </View>
                </View>
              </View>
              {lineItems.length > 1 && (
                <TouchableOpacity style={styles.deleteItemBtn} onPress={() => deleteLineItem(idx)}>
                  <Icon name="trash-2" size={16} color={colors.red} />
                </TouchableOpacity>
              )}
            </View>
          ))}
        </View>
        
        {/* Labor & Materials - Editable */}
        <View style={styles.laborMaterialsSection}>
          <Text style={styles.laborMaterialsTitle}>Additional Charges</Text>
          
          <View style={styles.laborMaterialsRow}>
            <View style={styles.laborMaterialsField}>
              <Text style={styles.laborMaterialsLabel}>Labor Hours</Text>
              <View style={styles.laborMaterialsInputRow}>
                <TextInput
                  style={styles.laborMaterialsInput}
                  placeholder="0"
                  placeholderTextColor={colors.gray}
                  value={laborHoursStr || (laborHours > 0 ? laborHours.toString() : '')}
                  onChangeText={handleLaborHoursChange}
                  keyboardType={isWeb ? 'default' : 'decimal-pad'}
                />
                <Text style={styles.laborMaterialsUnit}>hrs</Text>
              </View>
            </View>
            
            <View style={styles.laborMaterialsField}>
              <Text style={styles.laborMaterialsLabel}>Rate ($/hr)</Text>
              <TextInput
                style={styles.laborMaterialsInput}
                placeholder="75"
                placeholderTextColor={colors.gray}
                value={laborRateStr || (laborRate > 0 ? laborRate.toString() : '')}
                onChangeText={handleLaborRateChange}
                keyboardType={isWeb ? 'default' : 'decimal-pad'}
              />
            </View>
            
            <View style={styles.laborMaterialsField}>
              <Text style={styles.laborMaterialsLabel}>Labor Total</Text>
              <Text style={styles.laborMaterialsTotalValue}>${laborTotal.toFixed(2)}</Text>
            </View>
          </View>
          
          <View style={styles.laborMaterialsRow}>
            <View style={[styles.laborMaterialsField, { flex: 1 }]}>
              <Text style={styles.laborMaterialsLabel}>Materials Cost</Text>
              <View style={styles.laborMaterialsInputRow}>
                <Text style={styles.laborMaterialsDollar}>$</Text>
                <TextInput
                  style={[styles.laborMaterialsInput, { flex: 1 }]}
                  placeholder="0.00"
                  placeholderTextColor={colors.gray}
                  value={materialsCostStr || (materialsCost > 0 ? materialsCost.toString() : '')}
                  onChangeText={handleMaterialsCostChange}
                  keyboardType={isWeb ? 'default' : 'decimal-pad'}
                />
              </View>
            </View>
          </View>
        </View>
        
        {/* Totals */}
        <View style={styles.invoiceTotals}>
          <View style={styles.invoiceTotalRow}>
            <Text style={styles.invoiceTotalLabel}>Subtotal</Text>
            <Text style={styles.invoiceTotalValue}>${subtotal.toFixed(2)}</Text>
          </View>
          
          {(testimonialDiscount > 0 || discount > 0) && (
            <View style={styles.invoiceTotalRow}>
              <Text style={[styles.invoiceTotalLabel, { color: colors.green }]}>Discount</Text>
              <Text style={[styles.invoiceTotalValue, { color: colors.green }]}>-${(testimonialDiscount + discount).toFixed(2)}</Text>
            </View>
          )}
          
          <View style={[styles.invoiceTotalRow, styles.invoiceTotalFinal]}>
            <Text style={styles.invoiceTotalLabelFinal}>Total Due</Text>
            <Text style={styles.invoiceTotalValueFinal}>${total.toFixed(2)}</Text>
          </View>
        </View>
      </View>
      
      {/* Testimonial Option */}
      <View style={styles.testimonialOption}>
        <View style={styles.testimonialOptionHeader}>
          <Icon name="star" size={20} color={colors.gold} />
          <Text style={styles.testimonialOptionTitle}>Leave a Review & Save!</Text>
        </View>
        <Text style={styles.testimonialOptionDesc}>
          {paymentSettings?.testimonialPrompt?.replace('{discount}', paymentSettings?.testimonialDiscount) || 
            `Leave us a review and save ${paymentSettings?.testimonialDiscount || 10}% on your bill!`}
        </Text>
        
        <View style={styles.testimonialButtons}>
          <TouchableOpacity 
            style={[styles.testimonialBtn, wantsTestimonial && styles.testimonialBtnActive]}
            onPress={() => setWantsTestimonial(true)}
          >
            <Icon name="check-circle" size={16} color={wantsTestimonial ? colors.darkNavy : colors.teal} />
            <Text style={[styles.testimonialBtnText, wantsTestimonial && styles.testimonialBtnTextActive]}>
              Yes, Save {paymentSettings?.testimonialDiscount || 10}%
            </Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.testimonialBtn, !wantsTestimonial && styles.testimonialBtnInactive]}
            onPress={() => setWantsTestimonial(false)}
          >
            <Text style={styles.testimonialBtnTextSecondary}>No Thanks</Text>
          </TouchableOpacity>
        </View>
      </View>
      
      <TouchableOpacity 
        style={styles.primaryBtn}
        onPress={() => setStep(wantsTestimonial ? 2 : 3)}
      >
        <Text style={styles.primaryBtnText}>
          {wantsTestimonial ? 'Continue to Review' : 'Continue to Payment'}
        </Text>
        <Icon name="arrow-right" size={18} color={colors.darkNavy} />
      </TouchableOpacity>
    </View>
  );
  
  // Step 2: Leave Testimonial
  const renderTestimonialStep = () => (
    <View style={styles.stepContainer}>
      <View style={styles.testimonialCard}>
        <View style={styles.testimonialCardHeader}>
          <Icon name="star" size={24} color={colors.gold} />
          <Text style={styles.testimonialCardTitle}>Share Your Experience</Text>
        </View>
        
        {/* Star Rating */}
        <View style={styles.ratingContainer}>
          <Text style={styles.ratingLabel}>How would you rate our service?</Text>
          <View style={styles.starsRow}>
            {[1, 2, 3, 4, 5].map((star) => (
              <TouchableOpacity key={star} onPress={() => setRating(star)}>
                <Text style={[styles.star, star <= rating && styles.starActive]}>★</Text>
              </TouchableOpacity>
            ))}
          </View>
          <Text style={styles.ratingText}>
            {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
          </Text>
        </View>
        
        {/* Review Text */}
        <View style={styles.reviewInputContainer}>
          <Text style={styles.reviewLabel}>Tell us about your experience</Text>
          <TextInput
            style={styles.reviewInput}
            placeholder="What did you like about our service? How was the quality of work?"
            placeholderTextColor={colors.gray}
            value={reviewText}
            onChangeText={setReviewText}
            multiline
            numberOfLines={4}
          />
        </View>
        
        {/* Discount Badge */}
        <View style={styles.discountBadge}>
          <Icon name="tag" size={16} color={colors.green} />
          <Text style={styles.discountBadgeText}>
            You're saving ${testimonialDiscount.toFixed(2)} with your review!
          </Text>
        </View>
      </View>
      
      <View style={styles.stepButtons}>
        <TouchableOpacity style={styles.backBtn} onPress={() => setStep(1)}>
          <Icon name="arrow-left" size={18} color={colors.teal} />
          <Text style={styles.backBtnText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.primaryBtn, { flex: 1 }]}
          onPress={() => setStep(3)}
        >
          <Text style={styles.primaryBtnText}>Continue to Payment</Text>
          <Icon name="arrow-right" size={18} color={colors.darkNavy} />
        </TouchableOpacity>
      </View>
    </View>
  );
  
  // Step 3: Payment
  const renderPaymentStep = () => (
    <View style={styles.stepContainer}>
      {/* Final Amount */}
      <View style={styles.paymentAmountCard}>
        <Text style={styles.paymentAmountLabel}>Amount Due</Text>
        <Text style={styles.paymentAmountValue}>${total.toFixed(2)}</Text>
        {testimonialDiscount > 0 && (
          <View style={styles.paymentSavings}>
            <Icon name="check-circle" size={14} color={colors.green} />
            <Text style={styles.paymentSavingsText}>You saved ${testimonialDiscount.toFixed(2)} with your review!</Text>
          </View>
        )}
      </View>
      
      {/* Payment Methods */}
      <View style={styles.paymentMethods}>
        <Text style={styles.paymentMethodsTitle}>Select Payment Method</Text>
        
        {/* Digital Payments */}
        <View style={styles.paymentMethodGroup}>
          {paymentSettings?.venmoHandle && (
            <TouchableOpacity 
              style={[styles.paymentMethodBtn, selectedPaymentMethod === 'venmo' && styles.paymentMethodBtnActive]}
              onPress={() => setSelectedPaymentMethod('venmo')}
            >
              <View style={[styles.paymentMethodIcon, { backgroundColor: '#3D95CE20' }]}>
                <Text style={styles.paymentMethodEmoji}>💳</Text>
              </View>
              <View style={styles.paymentMethodInfo}>
                <Text style={styles.paymentMethodName}>Venmo</Text>
                <Text style={styles.paymentMethodHandle}>{paymentSettings.venmoHandle}</Text>
              </View>
              {selectedPaymentMethod === 'venmo' && <Icon name="check-circle" size={20} color={colors.teal} />}
            </TouchableOpacity>
          )}
          
          {paymentSettings?.cashappHandle && (
            <TouchableOpacity 
              style={[styles.paymentMethodBtn, selectedPaymentMethod === 'cashapp' && styles.paymentMethodBtnActive]}
              onPress={() => setSelectedPaymentMethod('cashapp')}
            >
              <View style={[styles.paymentMethodIcon, { backgroundColor: '#00D63220' }]}>
                <Text style={styles.paymentMethodEmoji}>💵</Text>
              </View>
              <View style={styles.paymentMethodInfo}>
                <Text style={styles.paymentMethodName}>Cash App</Text>
                <Text style={styles.paymentMethodHandle}>{paymentSettings.cashappHandle}</Text>
              </View>
              {selectedPaymentMethod === 'cashapp' && <Icon name="check-circle" size={20} color={colors.teal} />}
            </TouchableOpacity>
          )}
          
          {paymentSettings?.zelleEmail && (
            <TouchableOpacity 
              style={[styles.paymentMethodBtn, selectedPaymentMethod === 'zelle' && styles.paymentMethodBtnActive]}
              onPress={() => setSelectedPaymentMethod('zelle')}
            >
              <View style={[styles.paymentMethodIcon, { backgroundColor: '#6D1ED420' }]}>
                <Text style={styles.paymentMethodEmoji}>🏦</Text>
              </View>
              <View style={styles.paymentMethodInfo}>
                <Text style={styles.paymentMethodName}>Zelle</Text>
                <Text style={styles.paymentMethodHandle}>{paymentSettings.zelleEmail}</Text>
              </View>
              {selectedPaymentMethod === 'zelle' && <Icon name="check-circle" size={20} color={colors.teal} />}
            </TouchableOpacity>
          )}
          
          <TouchableOpacity 
            style={[styles.paymentMethodBtn, selectedPaymentMethod === 'cash' && styles.paymentMethodBtnActive]}
            onPress={() => setSelectedPaymentMethod('cash')}
          >
            <View style={[styles.paymentMethodIcon, { backgroundColor: colors.green + '20' }]}>
              <Text style={styles.paymentMethodEmoji}>💰</Text>
            </View>
            <View style={styles.paymentMethodInfo}>
              <Text style={styles.paymentMethodName}>Cash</Text>
              <Text style={styles.paymentMethodHandle}>Pay in person</Text>
            </View>
            {selectedPaymentMethod === 'cash' && <Icon name="check-circle" size={20} color={colors.teal} />}
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={[styles.paymentMethodBtn, selectedPaymentMethod === 'check' && styles.paymentMethodBtnActive]}
            onPress={() => setSelectedPaymentMethod('check')}
          >
            <View style={[styles.paymentMethodIcon, { backgroundColor: colors.orange + '20' }]}>
              <Text style={styles.paymentMethodEmoji}>📝</Text>
            </View>
            <View style={styles.paymentMethodInfo}>
              <Text style={styles.paymentMethodName}>Check</Text>
              <Text style={styles.paymentMethodHandle}>Pay by check</Text>
            </View>
            {selectedPaymentMethod === 'check' && <Icon name="check-circle" size={20} color={colors.teal} />}
          </TouchableOpacity>
        </View>
      </View>
      
      <View style={styles.stepButtons}>
        <TouchableOpacity style={styles.backBtn} onPress={() => setStep(wantsTestimonial ? 2 : 1)}>
          <Icon name="arrow-left" size={18} color={colors.teal} />
          <Text style={styles.backBtnText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.primaryBtn, { flex: 1 }, !selectedPaymentMethod && styles.primaryBtnDisabled]}
          onPress={handlePaymentComplete}
          disabled={!selectedPaymentMethod || processing}
        >
          {processing ? (
            <ActivityIndicator size="small" color={colors.darkNavy} />
          ) : (
            <>
              <Text style={styles.primaryBtnText}>Complete Payment</Text>
              <Icon name="check" size={18} color={colors.darkNavy} />
            </>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
  
  // Step 4: Success
  const renderSuccessStep = () => (
    <View style={styles.successContainer}>
      <View style={styles.successIcon}>
        <Icon name="check-circle" size={64} color={colors.green} />
      </View>
      <Text style={styles.successTitle}>Payment Complete!</Text>
      <Text style={styles.successSubtitle}>Thank you for choosing HandyHawk</Text>
      
      {testimonialDiscount > 0 && (
        <View style={styles.successSavingsBadge}>
          <Icon name="tag" size={16} color={colors.green} />
          <Text style={styles.successSavingsText}>You saved ${testimonialDiscount.toFixed(2)} with your review!</Text>
        </View>
      )}
      
      <View style={styles.successReceipt}>
        <Text style={styles.successReceiptTitle}>Receipt</Text>
        <View style={styles.successReceiptRow}>
          <Text style={styles.successReceiptLabel}>Amount Paid</Text>
          <Text style={styles.successReceiptValue}>${total.toFixed(2)}</Text>
        </View>
        <View style={styles.successReceiptRow}>
          <Text style={styles.successReceiptLabel}>Payment Method</Text>
          <Text style={styles.successReceiptValue}>{selectedPaymentMethod}</Text>
        </View>
        <View style={styles.successReceiptRow}>
          <Text style={styles.successReceiptLabel}>Date</Text>
          <Text style={styles.successReceiptValue}>{new Date().toLocaleDateString()}</Text>
        </View>
      </View>
      
      <Text style={styles.successMessage}>
        {paymentSettings?.customMessage || 'Thank you for your business! We appreciate you choosing HandyHawk for your home improvement needs.'}
      </Text>
      
      <TouchableOpacity 
        style={styles.successCloseBtn}
        onPress={() => {
          if (isWeb) window.close();
        }}
      >
        <Text style={styles.successCloseBtnText}>Close</Text>
      </TouchableOpacity>
    </View>
  );
  
  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLogo}>
          <Text style={styles.headerLogoText}>HANDY</Text>
          <Text style={styles.headerLogoAccent}>HAWK</Text>
        </View>
        <View style={styles.stepIndicator}>
          {step < 4 && (
            <>
              <View style={[styles.stepDot, step >= 1 && styles.stepDotActive]} />
              {wantsTestimonial && <View style={[styles.stepDot, step >= 2 && styles.stepDotActive]} />}
              <View style={[styles.stepDot, step >= 3 && styles.stepDotActive]} />
            </>
          )}
        </View>
      </View>
      
      {/* Content */}
      <ScrollView 
        style={styles.content} 
        contentContainerStyle={styles.contentInner}
        showsVerticalScrollIndicator={false}
      >
        <Animated.View style={{ opacity: fadeAnim }}>
          {step === 1 && renderReviewStep()}
          {step === 2 && renderTestimonialStep()}
          {step === 3 && renderPaymentStep()}
          {step === 4 && renderSuccessStep()}
        </Animated.View>
      </ScrollView>
      
      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>Secured by HandyHawk</Text>
        <View style={styles.footerIcons}>
          <Icon name="lock" size={14} color={colors.gray} />
          <Icon name="shield" size={14} color={colors.gray} />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.darkNavy,
    minHeight: '100vh',
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: colors.darkNavy,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  loadingContent: {
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    color: colors.gray,
    fontSize: 16,
  },
  
  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    backgroundColor: colors.navy,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
  },
  headerLogo: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  headerLogoText: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.white,
  },
  headerLogoAccent: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.gold,
  },
  stepIndicator: {
    flexDirection: 'row',
    gap: 8,
  },
  stepDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.navyLight,
  },
  stepDotActive: {
    backgroundColor: colors.teal,
  },
  
  // Content
  content: {
    flex: 1,
  },
  contentInner: {
    padding: 20,
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
  },
  stepContainer: {
    gap: 20,
  },
  
  // Invoice Card
  invoiceCard: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  invoiceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
  },
  invoiceDate: {
    fontSize: 14,
    color: colors.gray,
  },
  invoiceClientBox: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: colors.navyLight,
    borderRadius: 10,
  },
  invoiceClientLabel: {
    fontSize: 11,
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  invoiceClientName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  invoiceClientAddress: {
    fontSize: 14,
    color: colors.textMuted,
    marginTop: 4,
  },
  invoiceItems: {
    marginBottom: 20,
  },
  invoiceItemsHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  invoiceItemsSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  addLineItemBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: colors.teal + '15',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.teal + '30',
  },
  addLineItemBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.teal,
  },
  editableItemRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
  },
  editableItemMain: {
    flex: 1,
    gap: 12,
  },
  editableItemDescription: {
    backgroundColor: colors.navyLight,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    color: colors.white,
    fontSize: 15,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  editableItemNumbers: {
    flexDirection: 'row',
    gap: 10,
  },
  editableItemField: {
    flex: 1,
  },
  editableItemLabel: {
    fontSize: 11,
    color: colors.gray,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  editableItemInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: colors.white,
    fontSize: 15,
    textAlign: 'center',
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  editableItemTotal: {
    flex: 1,
    alignItems: 'center',
  },
  editableItemTotalValue: {
    backgroundColor: colors.teal + '15',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: colors.teal,
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  deleteItemBtn: {
    padding: 10,
    backgroundColor: colors.red + '15',
    borderRadius: 8,
    marginTop: 6,
  },
  
  // Labor & Materials Section
  laborMaterialsSection: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  laborMaterialsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 16,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  laborMaterialsRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  laborMaterialsField: {
    flex: 1,
  },
  laborMaterialsLabel: {
    fontSize: 11,
    color: colors.gray,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  laborMaterialsInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  laborMaterialsInput: {
    backgroundColor: colors.navy,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: colors.white,
    fontSize: 15,
    textAlign: 'center',
    borderWidth: 1,
    borderColor: colors.glassBorder,
    flex: 1,
  },
  laborMaterialsUnit: {
    fontSize: 13,
    color: colors.gray,
  },
  laborMaterialsDollar: {
    fontSize: 15,
    color: colors.gray,
    marginLeft: 4,
  },
  laborMaterialsTotalValue: {
    backgroundColor: colors.gold + '15',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: colors.gold,
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
    overflow: 'hidden',
  },
  
  // Legacy styles (kept for compatibility)
  invoiceItemHeader: {
    flexDirection: 'row',
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
    marginBottom: 8,
  },
  invoiceItemCol: {
    fontSize: 11,
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  invoiceItemRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder + '50',
  },
  invoiceItemText: {
    fontSize: 14,
    color: colors.text,
  },
  invoiceTotals: {
    borderTopWidth: 2,
    borderTopColor: colors.glassBorder,
    paddingTop: 16,
    marginTop: 8,
  },
  invoiceTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  invoiceTotalLabel: {
    fontSize: 14,
    color: colors.gray,
  },
  invoiceTotalValue: {
    fontSize: 14,
    color: colors.white,
    fontWeight: '500',
  },
  invoiceTotalFinal: {
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
    marginTop: 8,
    paddingTop: 16,
  },
  invoiceTotalLabelFinal: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  invoiceTotalValueFinal: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.teal,
  },
  
  // Testimonial Option
  testimonialOption: {
    backgroundColor: colors.gold + '15',
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: colors.gold + '30',
  },
  testimonialOptionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  testimonialOptionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.gold,
  },
  testimonialOptionDesc: {
    fontSize: 14,
    color: colors.text,
    marginBottom: 16,
    lineHeight: 22,
  },
  testimonialButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  testimonialBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: colors.teal + '20',
    borderWidth: 1,
    borderColor: colors.teal + '40',
  },
  testimonialBtnActive: {
    backgroundColor: colors.teal,
    borderColor: colors.teal,
  },
  testimonialBtnInactive: {
    backgroundColor: 'transparent',
    borderColor: colors.gray + '40',
  },
  testimonialBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.teal,
  },
  testimonialBtnTextActive: {
    color: colors.darkNavy,
  },
  testimonialBtnTextSecondary: {
    fontSize: 14,
    color: colors.gray,
  },
  
  // Primary Button
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    backgroundColor: colors.gold,
    paddingVertical: 16,
    borderRadius: 12,
  },
  primaryBtnText: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  primaryBtnDisabled: {
    opacity: 0.5,
  },
  
  // Back Button
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 12,
    backgroundColor: colors.teal + '15',
    borderWidth: 1,
    borderColor: colors.teal + '30',
  },
  backBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.teal,
  },
  stepButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  
  // Testimonial Card
  testimonialCard: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  testimonialCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
  },
  testimonialCardTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
  },
  ratingContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  ratingLabel: {
    fontSize: 14,
    color: colors.gray,
    marginBottom: 12,
  },
  starsRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  },
  star: {
    fontSize: 40,
    color: colors.navyLight,
  },
  starActive: {
    color: colors.gold,
  },
  ratingText: {
    fontSize: 16,
    color: colors.text,
    fontWeight: '500',
  },
  reviewInputContainer: {
    marginBottom: 16,
  },
  reviewLabel: {
    fontSize: 14,
    color: colors.gray,
    marginBottom: 8,
  },
  reviewInput: {
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    padding: 16,
    color: colors.white,
    fontSize: 15,
    minHeight: 120,
    textAlignVertical: 'top',
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  discountBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.green + '15',
    padding: 12,
    borderRadius: 10,
    justifyContent: 'center',
  },
  discountBadgeText: {
    fontSize: 14,
    color: colors.green,
    fontWeight: '500',
  },
  
  // Payment
  paymentAmountCard: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  paymentAmountLabel: {
    fontSize: 14,
    color: colors.gray,
    marginBottom: 8,
  },
  paymentAmountValue: {
    fontSize: 48,
    fontWeight: '700',
    color: colors.teal,
  },
  paymentSavings: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 12,
    backgroundColor: colors.green + '15',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  paymentSavingsText: {
    fontSize: 13,
    color: colors.green,
  },
  paymentMethods: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  paymentMethodsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 16,
  },
  paymentMethodGroup: {
    gap: 10,
  },
  paymentMethodBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    gap: 14,
  },
  paymentMethodBtnActive: {
    borderColor: colors.teal,
    backgroundColor: colors.teal + '10',
  },
  paymentMethodIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  paymentMethodEmoji: {
    fontSize: 22,
  },
  paymentMethodInfo: {
    flex: 1,
  },
  paymentMethodName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },
  paymentMethodHandle: {
    fontSize: 13,
    color: colors.gray,
    marginTop: 2,
  },
  
  // Success
  successContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  successIcon: {
    marginBottom: 24,
  },
  successTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 8,
  },
  successSubtitle: {
    fontSize: 16,
    color: colors.gray,
    marginBottom: 24,
  },
  successSavingsBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.green + '15',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginBottom: 24,
  },
  successSavingsText: {
    fontSize: 14,
    color: colors.green,
    fontWeight: '500',
  },
  successReceipt: {
    width: '100%',
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  successReceiptTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 16,
    textAlign: 'center',
  },
  successReceiptRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
  },
  successReceiptLabel: {
    fontSize: 14,
    color: colors.gray,
  },
  successReceiptValue: {
    fontSize: 14,
    color: colors.white,
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  successMessage: {
    fontSize: 14,
    color: colors.textMuted,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  successCloseBtn: {
    paddingVertical: 14,
    paddingHorizontal: 32,
    backgroundColor: colors.navyLight,
    borderRadius: 10,
  },
  successCloseBtnText: {
    fontSize: 15,
    color: colors.white,
    fontWeight: '500',
  },
  
  // Footer
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
    backgroundColor: colors.navy,
  },
  footerText: {
    fontSize: 12,
    color: colors.gray,
  },
  footerIcons: {
    flexDirection: 'row',
    gap: 8,
  },
});

export default CheckoutScreen;

